import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { EASE } from "@/lib/motion";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.4 });
  const posterY = useTransform(scrollYProgress, [0, 1], ["0%", "-6%"]);
  const posterScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.7, 0.95], [1, 1, 0]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      ref={ref}
      className="scroll-scrub"
      style={{ "--ss-accent": "#31664D", "--ss-bg": "#243D34", "--ss-ink": "#F1F4F3", "--ss-muted": "#E0E9E4" }}
      data-testid="hero"
    >
      <div className="scroll-scrub__stage">
        <div className="scroll-scrub__media" aria-hidden="true">
          <figure className="scroll-scrub__layer scroll-scrub__layer--scene">
            <motion.picture
              className="scroll-scrub__picture"
              initial={{ scale: 1.06 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.8, ease: EASE }}
            >
              <source
                media="(hover: none) and (pointer: coarse), (max-width: 860px)"
                srcSet="/assets/world/scene-01-portrait-poster.png"
              />
              <motion.img
                className="scroll-scrub__poster"
                style={{ y: posterY, scale: posterScale }}
                decoding="async"
                fetchpriority="high"
                src="/assets/world/scene-01-2k-poster.png"
                alt=""
              />
            </motion.picture>
          </figure>
        </div>
        <div className="scroll-scrub__progress" aria-hidden="true">
          <motion.span style={{ scaleX: progress }} />
        </div>
      </div>
      <div className="scroll-scrub__story">
        <article className="scroll-scrub__chapter" id="meer-ruimte" style={{ minHeight: "240dvh" }}>
          <div className="scroll-scrub__chapter-pin">
            <motion.div className="scroll-scrub__copy" style={{ opacity: copyOpacity, y: copyY }}>
              <motion.p
                className="scroll-scrub__kicker"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              >
                Ontruiming &amp; Hergebruik
              </motion.p>
              <h1 className="scroll-scrub__title">
                <span className="line-mask">
                  <motion.span
                    initial={{ y: "112%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, delay: 0.22, ease: EASE }}
                  >
                    Meer ruimte.
                  </motion.span>
                </span>
                <span className="line-mask">
                  <motion.span
                    initial={{ y: "112%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, delay: 0.36, ease: EASE }}
                  >
                    Minder zorgen.
                  </motion.span>
                </span>
              </h1>
              <motion.p
                className="scroll-scrub__body"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
              >
                Garages, zolders en bergingen uitruimen in Laarbeek en omgeving.
              </motion.p>
              <motion.div
                className="scroll-scrub__actions"
                initial={{ opacity: 0, scale: 0.88, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 18, delay: 0.75 }}
              >
                <a href="#offerte" className="hero-stamp" data-testid="hero-cta">
                  Offerte aanvragen<span aria-hidden="true">↗</span>
                </a>
              </motion.div>
            </motion.div>
          </div>
        </article>
      </div>
    </section>
  );
}
